import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import { useGetForm } from 'src/shared/lib/hooks/useGetForm';
import { useGetFormsList } from 'src/shared/lib/hooks/useGetFormsList';
import { Oferta } from 'src/shared/ui';
import { ProjectTablePage } from 'src/widgets/ProjectTable';
import { Sidebar } from 'src/widgets/Sidebar';

const normalizeAlias = (alias?: string) =>
  decodeURIComponent(alias || '')
    .trim()
    .toLowerCase();
const parsePositiveInt = (rawValue: string | null): number | null => {
  if (!rawValue) {
    return null;
  }

  const parsedValue = Number.parseInt(rawValue, 10);
  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return null;
  }

  return parsedValue;
};

export const TablePage: FC = () => {
  const [fullWidth, setFullWidth] = useState<boolean>(false);
  const [width, setWidth] = useState<number>(320);
  const { formAlias } = useParams<{ formAlias?: string }>();
  const [searchParams] = useSearchParams();
  const { projects, forms, selectedForm, selectedProject } = useContext(ProjectsContext);
  const { handleSelectProject } = useGetFormsList();
  const { handleSelectForm } = useGetForm();

  const aliasFromUrl = useMemo(() => normalizeAlias(formAlias), [formAlias]);
  const projectIdFromUrl = useMemo(
    () => parsePositiveInt(searchParams.get('projectId')),
    [searchParams],
  );
  const formIdFromUrl = useMemo(() => parsePositiveInt(searchParams.get('formId')), [searchParams]);

  useEffect(() => {
    if (!aliasFromUrl || !projects.length || (projectIdFromUrl && formIdFromUrl)) {
      return;
    }

    if (selectedForm && normalizeAlias(selectedForm.form_name) === aliasFromUrl) {
      return;
    }

    let cancelled = false;

    const syncFormByAlias = async () => {
      let projectForms = forms;
      let projectToSelect = selectedProject;

      const hasFormInCurrentProject = projectForms.some(
        form => normalizeAlias(form.form_name) === aliasFromUrl,
      );

      if (!hasFormInCurrentProject) {
        const matchedProject = projects.find(project =>
          project.forms?.some(form => normalizeAlias(form.form_name) === aliasFromUrl),
        );

        if (!matchedProject) {
          return;
        }

        const shouldReloadForms = !projectToSelect || projectToSelect.id !== matchedProject.id;
        if (shouldReloadForms) {
          projectForms = await handleSelectProject(matchedProject, {
            keepAliasRoute: true,
            ensureFormsLoaded: true,
          });
          projectToSelect = matchedProject;
        } else {
          projectForms = forms;
        }
      }

      if (cancelled) {
        return;
      }

      const formToSelect = projectForms.find(
        form => normalizeAlias(form.form_name) === aliasFromUrl,
      );

      if (!formToSelect) {
        return;
      }

      await handleSelectForm(formToSelect, { syncUrl: false });
    };

    syncFormByAlias();

    return () => {
      cancelled = true;
    };
  }, [
    aliasFromUrl,
    forms,
    formIdFromUrl,
    handleSelectForm,
    handleSelectProject,
    projectIdFromUrl,
    projects,
    selectedForm,
    selectedProject,
  ]);

  useEffect(() => {
    if (!projectIdFromUrl || !formIdFromUrl || !projects.length) {
      return;
    }

    if (selectedProject?.id === projectIdFromUrl && selectedForm?.id === formIdFromUrl) {
      return;
    }

    let cancelled = false;

    const syncFormByIds = async () => {
      const targetProject = projects.find(project => project.id === projectIdFromUrl);
      if (!targetProject) {
        return;
      }

      const shouldReloadForms =
        !selectedProject || selectedProject.id !== targetProject.id || !forms.length;
      const projectForms = shouldReloadForms
        ? await handleSelectProject(targetProject, {
            keepAliasRoute: true,
            ensureFormsLoaded: true,
          })
        : forms;

      if (cancelled) {
        return;
      }

      const formToSelect = projectForms.find(form => form.id === formIdFromUrl);
      if (!formToSelect) {
        return;
      }

      await handleSelectForm(formToSelect, { syncUrl: false });
    };

    syncFormByIds();

    return () => {
      cancelled = true;
    };
  }, [
    formIdFromUrl,
    forms,
    handleSelectForm,
    handleSelectProject,
    projectIdFromUrl,
    projects,
    selectedForm,
    selectedProject,
  ]);

  return (
    <div className="relative flex w-full">
      {!fullWidth && <Sidebar setWidth={setWidth} />}

      <div className="flex h-full w-full flex-col bg-white-100">
        <ProjectTablePage fullWidth={fullWidth} width={width} setFullWidth={setFullWidth} />

        <Oferta classNames="px-2.5" />
      </div>
    </div>
  );
};
